'use client'

// Next Imports
import dynamic from 'next/dynamic'

// MUI Imports
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import Skeleton from '@mui/material/Skeleton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import type { ApexOptions } from 'apexcharts'

// Types
import type { BankAccountsChartProps } from '@/types/dashboards/resumeTypes'

// Hooks Imports
import { useDashboardResumeTranslations } from '@/hooks/useTranslations'

// Styled Component Imports
const AppReactApexCharts = dynamic(() => import('@/libs/styles/AppReactApexCharts'))

// Componente Skeleton para o gráfico de contas
const BankAccountsChartSkeleton = () => {
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
        <Skeleton variant="text" width={160} height={28} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={80} height={20} />
      </Box>

      {/* Barras horizontais */}
      {[...Array(4)].map((_, index) => (
        <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: `${90 - index * 15}%` }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={32}
              sx={{ borderRadius: 1 }}
            />
          </Box>
          <Skeleton variant="text" width={60} />
        </Box>
      ))}
    </Box>
  )
}

// Função para formatar valor monetário
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const BankAccountsChart = ({ accountsData, loading = false }: BankAccountsChartProps) => {
  // Hooks
  const { t } = useDashboardResumeTranslations()
  const theme = useTheme()

  // Usar apenas dados da API - sem dados mock/fallback
  const accounts = accountsData || []

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <BankAccountsChartSkeleton />
        </CardContent>
      </Card>
    )
  }

  // Se não há dados, exibir mensagem
  if (!accounts || accounts.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={t('bankAccounts.title')}
          sx={{ pb: 1 }}
        />
        <CardContent sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Dados não disponíveis
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Ordenar por valor absoluto (maiores saldos primeiro)
  const sortedAccounts = [...accounts].sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))

  // Preparar dados para o gráfico
  const series = [
    {
      name: t('bankAccounts.balance'),
      data: sortedAccounts.map(item => item.balance)
    }
  ]

  // Calcular o saldo total
  const totalBalance = sortedAccounts.reduce((sum, item) => sum + item.balance, 0)

  // Configuração do gráfico
  const options: ApexOptions = {
    chart: {
      parentHeightOffset: 0,
      toolbar: { show: false },
      width: '100%'
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
        distributed: true,
        borderRadius: 6,
        borderRadiusApplication: 'end'
      }
    },
    colors: sortedAccounts.map(account =>
      account.balance >= 0
        ? 'var(--mui-palette-success-main)'
        : 'var(--mui-palette-error-main)'
    ),
    grid: {
      strokeDashArray: 6,
      borderColor: 'var(--mui-palette-divider)',
      xaxis: {
        lines: { show: true }
      },
      yaxis: {
        lines: { show: false }
      },
      padding: {
        top: -15,
        left: 15,
        right: 20,
        bottom: 8
      }
    },
    dataLabels: {
      enabled: true,
      offsetY: 6,
      style: {
        colors: ['var(--mui-palette-text-primary)'],
        fontWeight: 600,
        fontSize: '0.875rem'
      },
      formatter(val: string, opts: any) {
        const account = sortedAccounts[opts.dataPointIndex]

        return account.bank
      }
    },
    tooltip: {
      enabled: true,
      theme: theme.palette.mode,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const value = series[seriesIndex][dataPointIndex]
        const account = sortedAccounts[dataPointIndex]

        return `
          <div style="
            padding: 12px;
            min-width: 200px;
            background: ${theme.palette.background.default};
            border: 1px solid ${theme.palette.divider};
            border-radius: 6px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          ">
            <div style="
              font-weight: 600;
              font-size: 14px;
              color: ${theme.palette.text.primary};
              margin-bottom: 8px;
              padding-bottom: 8px;
              border-bottom: 1px solid ${theme.palette.divider};
            ">
              ${account.bank}
            </div>
            <div style="
              font-size: 13px;
              color: ${theme.palette.text.secondary};
              margin-bottom: 4px;
            ">
              ${account.accountName}
            </div>
            <div style="
              font-size: 13px;
              color: ${theme.palette.text.secondary};
              margin-bottom: 8px;
            ">
              Conta: ${account.accountNumber}
            </div>
            <div style="
              font-weight: 600;
              font-size: 14px;
              color: ${value >= 0 ? theme.palette.success.main : theme.palette.error.main};
            ">
              Saldo: ${formatCurrency(value)}
            </div>
          </div>
        `
      }
    },
    legend: { show: false },
    states: {
      hover: {
        filter: { type: 'lighten' }
      },
      active: {
        filter: { type: 'none' }
      }
    },
    xaxis: {
      axisTicks: { show: false },
      axisBorder: { show: false },
      categories: series[0].data.map(v => formatCurrency(v)),
      labels: {
        show: false,
        formatter: val => formatCurrency(Number(val)),
        style: {
          fontSize: '0.75rem',
          colors: 'var(--mui-palette-text-disabled)'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '0.75rem',
          colors: 'var(--mui-palette-text-secondary)'
        }
      }
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            width: '100%'
          },
          dataLabels: {
            style: {
              fontSize: '0.7rem'
            }
          }
        }
      }
    ]
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('bankAccounts.title')}
        subheader={`${t('bankAccounts.totalBalance')}: ${formatCurrency(totalBalance)}`}
        sx={{ pb: 1 }}
      />
      <CardContent sx={{
        flex: 1,
        display: 'flex',
        pt: 0
      }}>
        <AppReactApexCharts
          type='bar'
          height='100%'
          width='100%'
          series={series}
          options={options}
        />
      </CardContent>
    </Card>
  )
}

export default BankAccountsChart
